import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

export default function PortfolioManager() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", category: "Kitchen", description: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-portfolio"],
    queryFn: async () => (await api.get("/admin/portfolio")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!file) { toast.error("Please choose an image"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("description", form.description);
      fd.append("image", file);
      await api.post("/admin/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Project added");
      setForm({ title: "", category: "Kitchen", description: "" });
      setFile(null);
      refresh();
    } catch {
      toast.error("Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/portfolio/${item.id}`, { visible: !item.visible });
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/portfolio/${id}`);
      toast.success("Project deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const inputCls = "w-full border border-sandline bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-orange";

  return (
    <div data-testid="admin-portfolio">
      <h1 className="font-heading font-bold text-2xl tracking-tight mb-6">Portfolio Manager</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-sandline p-6 space-y-4 h-fit" data-testid="portfolio-form">
          <h2 className="font-heading font-bold tracking-tight">Add Project</h2>
          <input required placeholder="Project title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="portfolio-title" className={inputCls} />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="portfolio-category" className={inputCls}>
            {["Kitchen", "Bathroom", "Exterior", "Full Remodel", "Flooring", "Painting", "Handyman", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="portfolio-description" className={`${inputCls} resize-none`} />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="portfolio-image-btn"
            className="w-full border-2 border-dashed border-sandline hover:border-orange p-6 flex flex-col items-center gap-2 text-sm text-slateblue transition-colors duration-200">
            <Upload size={20} className="text-orange" />
            {file ? file.name : "Choose project photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="portfolio-image-input" onChange={(e) => setFile(e.target.files[0])} />
          <button type="submit" disabled={saving} data-testid="portfolio-save"
            className="w-full bg-orange hover:bg-orange-bright disabled:opacity-60 text-white text-xs font-bold uppercase tracking-widest px-4 py-3 transition-colors duration-200">
            {saving ? "Uploading…" : "Add Project"}
          </button>
        </form>

        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isLoading ? (
            <p className="text-slateblue text-sm">Loading…</p>
          ) : (data || []).map((item) => (
            <div key={item.id} data-testid={`portfolio-row-${item.id.slice(0, 8)}`}
              className={`bg-white border border-sandline overflow-hidden ${item.visible ? "" : "opacity-55"}`}>
              {item.image_path ? (
                <img src={fileUrl(item.image_path)} alt={item.title} className="w-full aspect-[4/3] object-cover" />
              ) : (
                <img src={item.image_url} alt={item.title} className="w-full aspect-[4/3] object-cover" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-orange">{item.category}</span>
                    <p className="font-bold text-sm mt-0.5">{item.title}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggle(item)} data-testid={`portfolio-hide-${item.id.slice(0, 8)}`} className="p-1.5 text-slateblue hover:text-orange transition-colors duration-200">
                      {item.visible ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button onClick={() => remove(item.id)} data-testid={`portfolio-delete-${item.id.slice(0, 8)}`} className="p-1.5 text-slateblue hover:text-red-500 transition-colors duration-200">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
